import React, { useEffect, useRef } from 'react';
import type { Breakpoint } from '../../hooks/useBreakpoint';

const MOSAIC_COLORS = ['#4545FF', '#6B3FA0', '#00CED1', '#8B5CF6', '#4545FF', '#4545FF', '#6B3FA0'];
const GRID_SIZE = 28;
const RADIUS = 120;

interface MosaicBackgroundProps {
  breakpoint: Breakpoint;
}

const MosaicBackground: React.FC<MosaicBackgroundProps> = ({ breakpoint }) => {
  const wallRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wall = wallRef.current;
    if (!wall) return;

    wall.innerHTML = '';
    const cols = Math.ceil(window.innerWidth / (GRID_SIZE + 2));
    const rows = Math.ceil(window.innerHeight / (GRID_SIZE + 2));

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < cols * rows; i++) {
      const cell = document.createElement('div');
      cell.className = 'mosaic-cell';
      cell.style.background = MOSAIC_COLORS[Math.floor(Math.random() * MOSAIC_COLORS.length)];
      fragment.appendChild(cell);
    }
    wall.appendChild(fragment);

    // Skip mouse-follow on mobile/tablet for performance
    if (breakpoint === 'mobile' || breakpoint === 'tablet') {
      return () => { wall.innerHTML = ''; };
    }

    const cells = wall.querySelectorAll('.mosaic-cell');
    const onMouseMove = (e: MouseEvent) => {
      const mx = e.clientX;
      const my = e.clientY;
      cells.forEach((c) => {
        const el = c as HTMLElement;
        const r = el.getBoundingClientRect();
        const dx = mx - r.left - r.width / 2;
        const dy = my - r.top - r.height / 2;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < RADIUS * 0.5) {
          el.classList.add('active');
          el.classList.remove('fade');
        } else if (d < RADIUS) {
          el.classList.remove('active');
          el.classList.add('fade');
        } else {
          el.classList.remove('active', 'fade');
        }
      });
    };

    document.addEventListener('mousemove', onMouseMove);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      wall.innerHTML = '';
    };
  }, [breakpoint]);

  return (
    <div
      ref={wallRef}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, ${GRID_SIZE}px)`,
        gridTemplateRows: `repeat(auto-fill, ${GRID_SIZE}px)`,
        gap: '2px',
        padding: '2px',
      }}
    />
  );
};

export default MosaicBackground;
